import React, { useEffect, useState } from 'react';
import Spacing from '../Spacing';
import { FilePlus2, Link, Loader, Search, SquareCheckBig, TicketCheck } from 'lucide-react';
import DataTablePagination from '../DataTablePagination';
import { can_visit, getPractice, notify } from '../../helpers';
import { useDocumentTitle } from '../../hooks/customDocumentTitleHook';
import FormViewDetailsModal from '../Modal/FormViewDetailsModal';
import InvoicesDataTable from '../DataTable/InvoicesDataTable';
import { checkoutInvoicePayment, createPaymentLink, deleteInvoice, fetchCodingBillingData, getInvoicesData, getSubscriptionData, subscriptionPackage, unsubscriptionPackage } from '../../services/billing';
import Card from '../Card';
import { useLocation } from 'react-router-dom';
import StatusViewModal from '../Modal/StatusViewModal';
import Grid from '../Grid';
import Badge from '../UI/Badge';
import BillingOverViewSection from '../Section/BillingOverViewSection';
import CreateInvoiceModal from '../Modal/CreateInvoiceModal';
import GenerateBillViewModal from '../Modal/GenerateBillViewModal';
import CancelSubscriptionModal from '../Modal/CancelSubscriptionModal';

export default function Billing({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [packageData, setPackageData] = useState({});
  const [invoiceList, setInvoiceList] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [formViewId, setFormViewId] = useState(null);
  const [formView, setFormView] = useState(false);
  const [pageLoader, setPageLoader] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const [unsubscribeDocpro, setUnsubscribeDocpro] = useState(false);
  const [confirmUnsubscribeLoader, setConfirmUnsubscribeLoader] = useState(false);
  const [billingOverviewLoader, setBillingOverviewLoader] = useState(true);
  const [invoiceCreate, setInvoiceCreate] = useState(false);
  const [invoiceEditData, setInvoiceEditData] = useState({});
  const [invoiceEdit, setInvoiceEdit] = useState(false);
  const [generateBillLoader, setGenerateBillLoader] = useState(false);
  const [generateBillOpen, setGenerateBillOpen] = useState(false);
  const [billLink, setBillLink] = useState({});
  const [practiceBillingData, setPracticeBillingData] = useState({});
  const [billingOverviewDataCounts, setBillingOverviewDataCounts] =
    useState({
      latest_due_date: 0,
      total_coding_done_count: 0,
      total_charting_done_count: 0,
      total_due_count: 0,
    });

  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    search_term: "",
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const subscriptionSession = queryParams.get('session_id');
  const subscriptionPaymentStatus = queryParams.get('payment');
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  useEffect(() => {
    if (subscriptionSession !== null && !hasCheckedStatus) {
      setPaymentStatus(true);
      setPreviousScrollPosition(window.scrollY);
      window.scrollTo(0, 0);
      setHasCheckedStatus(true);
    }
  }, [subscriptionSession, hasCheckedStatus]);

  const getChartingCodingData = async () => {
    const response = await fetchCodingBillingData( account_id, location_id );
    if (response) {
      if(response?.detail) {
        notify("error", response?.detail);
      } else {
        setPracticeBillingData(response?.data);
        setInvoiceCreate(!invoiceCreate);
        setPreviousScrollPosition(window.scrollY);
        window.scrollTo(0, 0);
        setGenerateBillLoader(false);
      }
    }
  }

  const loadSubscription = async () => {
    setPageLoader(true);
    const getProductData = {
      account_id: account_id,
    };
    try {
      const response = await getSubscriptionData(getProductData);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          setPackageData(response?.data);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    }
  };

  const loadAllInvoices = async (filter) => {
    setFilter(filter);
    setPageLoader(true);
    setBillingOverviewLoader(true);
    let isCanceled = false;

    try {
      const response = await getInvoicesData( filter, account_id, location_id );
      const updatedData = response.data?.results?.map((row) => ({
        ...row,
        checked: false,
      }));
      setInvoiceList(updatedData);
      setPaginationCount({
        totalCount: response.data.count,
        totalPages: response.data.total_pages,
      });
      setBillingOverviewDataCounts({
        latest_due_date: response?.data?.latest_due_date,
        total_coding_done_count: response?.data?.total_coding_done,
        total_charting_done_count: response?.data?.total_charting_done,
        total_due_count: response?.data?.total_due,
      });
      setBillingOverviewLoader(false);
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail, 0.5);
        }
      }
    }
    setPageLoader(false);
    if (!isCanceled) {
      setDataLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeSearchTerm = (event) => {
    setFilter({ ...filter, search_term: event.target.value });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setPageLoader(true);
    setDataLoading(true);
    loadAllBillings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useEffect(() => {
    loadAllInvoices(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleFormView = (formId) => {
    setFormViewId(formId)
    setFormView(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const handleFormPrint = (formId) => {
    const url = `/invoice/print?form_id=${formId}&size=A4`;
    window.open(url, '_blank');
  };

  const loadAllBillings = async () => {
    await loadSubscription();
    await loadAllInvoices(filter);
  };

  const handleInvoiceEdit = (data) => {
    setInvoiceEditData(data);
    setInvoiceEdit(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const confirmSubscribe = async () => {
    setPageLoader(true);
    const subscribeData = {
      account_id: account_id
    };
    try {
      const response = await subscriptionPackage(subscribeData);
      if(response){
        if(response?.data?.payment_status === "unpaid" && response?.data?.status === "open"){
          window.location.href = response?.data?.url;
        } else {
          notify("error", response?.detail);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    }
    setPageLoader(false);
  }

  const unsubscribePackage = () => {
    setUnsubscribeDocpro(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  }

  const confirmUnsubscribe = async (cancelReason) => {
    if(!packageData?.subscription_id){
      notify("error", 'Subscription ID Not Found');
      return;
    }
    
    const unsubscribeData = {
      account_id: account_id,
      subscription_id: packageData?.subscription_id,
      cancel_comment: cancelReason
    };
    try {
      const response = await unsubscriptionPackage(unsubscribeData);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          setTimeout(() => {
            loadAllBillings();
          }, 2000);
          setUnsubscribeDocpro(false);
          window.scrollTo(0, previousScrollPosition);
          notify("success", response?.data?.message);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    }
    setConfirmUnsubscribeLoader(false);
  }

  const generatePaymentLink = async (priceId, quantity, type) => {
    if(!priceId){
      notify("error", 'Price ID Not Found');
      return;
    }
    const subscribeData = {
      account_id: account_id,
      price_id: priceId,
      quantity: quantity,
      mode: type,
    };
    try {
      const response = await createPaymentLink(subscribeData);
      if(response){
        if(response?.data){
          setBillLink(response?.data);
          setGenerateBillOpen(true);
        } else {
          notify("error", response?.detail);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    }
  }

  const handlePayInvoice = async (invoiceId) => {
    const payInvoiceData = {
      account_id: account_id,
      invoice_id: invoiceId
    };
    try {
      const response = await checkoutInvoicePayment(payInvoiceData);
      if(response){
        if(response?.data){
          window.location.href = response?.data?.url;
        } else {
          notify("error", response?.detail);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    }
  }

  const LimitText = ({ text, limit }) => {
    if (text?.length <= limit) {
      return text;
    } else {
      const limitText = text?.substring(0, limit) + "...";
      return limitText;
    }
  };

  const handleDeleteInvoice = async (id) => {
    setDataLoading(true);
    try {
      const result = await deleteInvoice(id, account_id, location_id);
      
      if(result){
        if(result?.data?.status === 'success'){
          notify("success", result?.data?.message, 0.5);
          loadAllInvoices(filter);
        } else {
          notify("error", result?.data?.message, 0.5);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    }
    setDataLoading(false);
  };

  const createNewBillingInvoice = async () => {
    setGenerateBillLoader(true);
    getChartingCodingData();
  }

  useDocumentTitle("Billing");

  return (
    <>
      <div className='relative'>
        <div className="docpro_page_heading_wrap">
          <h2 className="docpro_page_heading docpro_fs_24">
            <i className="docpro_blue_bg docpro_white_color">
              <TicketCheck width={18} height={18} />
            </i>
            Billing
          </h2>
        </div>

        {!packageData?.active ?
          can_visit("billing_create_subscription") && (
            <>
              <Card variant="docpro_card docpro_card_1">
                <div className='docpro_pricing_card relative'>
                  <h3 className="docpro_fs_24 mb-0 text-center" style={{ paddingBottom: '12px' }}>
                    {packageData?.title?.replace(/^1 x\s*/, '')}
                  </h3>

                  <div className='subscription_details'>
                    <div>
                      <div className='package_price'>
                        <div className='flex amount_div'>
                          <p className="docpro_fw_500 docpro_fs_24 docpro_primary_color docpro_mb_0">
                            $
                          </p>
                          <p className="docpro_primary_color docpro_mb_0 docpro_fw_700 price_amout">
                            {packageData?.amount || 0}
                          </p>
                        </div>
                        <div className='package_type'>
                          <div></div>
                          <p className='docpro_mb_0'><span>Per Month</span></p>
                        </div>
                        <Spacing lg="20" sm="20" />

                        <button className='subscribe_button docpro_ripple_button' onClick={() => confirmSubscribe()}>
                          Subscribe Now
                        </button>
                      </div>
                    </div>

                    <div className='package_details'>
                      <h3 className="docpro_fs_20 docpro_mb_0 docpro_fw_500">
                        What's include in the price?
                      </h3>
                      <Grid variant="docpro_grid_gap_10 docpro_grid_col_2">
                        <ul className='docpro_mb_0 docpro_list_1'>
                          <li>
                            <SquareCheckBig height={16} width={16} className='mr-1 docpro_accent_color' /> Comprehensive Dashboard Overview for Key Metrics
                          </li>
                          <li>
                            <SquareCheckBig height={16} width={16} className='mr-1 docpro_accent_color' /> Centralized Patient Management and Scheduling System
                          </li>
                          <li>
                            <SquareCheckBig height={16} width={16} className='mr-1 docpro_accent_color' /> Real-Time Claims Submission and Payments Tracking
                          </li>
                          <li>
                            <SquareCheckBig height={16} width={16} className='mr-1 docpro_accent_color' /> Care Gaps Monitoring for Preventive Health Compliance
                          </li>
                        </ul>
                        <ul className='docpro_mb_0 docpro_list_1'>
                          <li>
                            <SquareCheckBig height={16} width={16} className='mr-1 docpro_accent_color' /> Lab Report Follow-Up Tracking to Ensure Timely Patient Care
                          </li>
                          <li>
                            <SquareCheckBig height={16} width={16} className='mr-1 docpro_accent_color' /> High-Risk Patient Identification and Compliance Monitoring
                          </li>
                          <li>
                            <SquareCheckBig height={16} width={16} className='mr-1 docpro_accent_color' /> Bulk Messaging Service for Group Patient Communications
                          </li>
                          <li>
                            <SquareCheckBig height={16} width={16} className='mr-1 docpro_accent_color' /> Pre-Built Forms and Documentation Management for Compliance
                          </li>
                        </ul>
                      </Grid>
                    </div>
                  </div>

                  <div className='absolute docpro_icon_btn overview' style={{ width: 'auto', height: 'auto', top: '-12px', right: '-12px' }}>
                    <Badge color="red" variant="py-1" square>Inactive</Badge>
                    <div className="docpro_tip">
                      <span>Subscription is inactive</span>
                    </div>
                  </div>
                </div>
              </Card>
              <Spacing lg="20" sm="20" />
            </>
          )
        :
          can_visit("administrator") && (
            <>
              <Card variant="docpro_card docpro_card_1">
                <div className='docpro_pricing_card relative'>
                  <Grid variant="docpro_grid_gap_20 docpro_grid_col_4">
                    <div className="billing_subscription_details">
                      <h3 className="docpro_fs_20 docpro_mb_0 text-center">
                        {packageData?.title?.replace(/^1 x\s*/, '')}
                      </h3>
                      <h3 className="docpro_fs_24 docpro_mb_0 text-center" style={{ marginBottom: 0 }}>
                        ${packageData?.amount || 0} <sup className='docpro_fw_400 docpro_fs_14'>Monthly</sup>
                      </h3>
                    </div>
                    <div className="billing_subscription_details">
                      <p className="docpro_mb_0 docpro_ternary_color"> -- Timeline </p>
                      <p className="docpro_mb_0">
                        <b><span className='inline-flex' style={{ minWidth: '115px' }}>Activision Date</span>: </b>{packageData?.active_date}
                      </p>
                      <p className="docpro_mb_0">
                        <b><span className='inline-flex' style={{ minWidth: '115px' }}>Expired Date</span>: </b>{packageData?.renew_date}
                      </p>
                    </div>
                    <div className='package_unsubscribe billing_subscription_details'>
                      <p className="docpro_mb_0 docpro_ternary_color"> -- Manage Subscription </p>
                      <div className="docpro_mb_0">
                        {packageData?.canceled_at !== null ?
                          <>
                            <p className="docpro_mb_0">
                              <b><span className='inline-flex' style={{ minWidth: '110px' }}>Canceled Date</span>: </b>{packageData?.canceled_at}
                            </p>
                            <div className="docpro_mb_0">
                              <span className="docpro_mb_0 pointer docpro_icon_btn overview docpro_red_color" style={{ width: 'auto', height: 'auto' }}>
                                {packageData?.cancel_comment !== null && (
                                  <>
                                    <LimitText text= {packageData?.cancel_comment} limit={40} />
                                    <div className="docpro_tip">
                                      <span> {packageData?.cancel_comment}</span>
                                    </div>
                                  </>
                                )}
                              </span>
                            </div>
                          </>
                        :
                          <>
                            If you wish to cancel your subscription to this package, please 
                            <span className='package_unsubscribe_link pointer docpro_icon_btn overview' style={{ width: 'auto', height: 'auto' }} onClick={unsubscribePackage}>click here
                              <div className="docpro_tip">
                                <span>Unsubscribe</span>
                              </div>
                            </span>
                            to proceed with cancellation.
                          </>
                        }
                      </div>
                        
                    </div>
                    <div className="billing_subscription_details">
                      {packageData?.canceled_at === null ?
                        <span
                          className="docpro_badge docpro_badge_green docpro_square py-1"
                          style={{
                            backgroundImage: 'url(https://i.ibb.co.com/m0wD1zM/bg.gif)',
                            backgroundBlendMode: 'color-dodge',
                            padding: '20px 25px',
                            fontSize: '18px',
                            userSelect: 'none',
                          }}
                        >
                          Active
                        </span>
                      :
                        < div className='flex'>
                          <span
                            className="docpro_badge docpro_badge_red docpro_square py-1 mr-2"
                            style={{
                              backgroundImage: 'url(https://i.ibb.co.com/m0wD1zM/bg.gif)',
                              backgroundBlendMode: 'color-dodge',
                              padding: '20px 25px',
                              fontSize: '18px',
                              userSelect: 'none',
                            }}
                          >
                            Canceled
                          </span>
                          <button className='subscribe_button docpro_ripple_button docpro_bg_success' style={{ borderRadius: '5px', padding: '18px' }} onClick={() => confirmSubscribe()}>
                            Re-subscribe
                          </button>
                        </div>
                      }
                      {/* <span
                        className="docpro_badge docpro_badge_red docpro_square py-1"
                        style={{
                          padding: '20px 25px',
                          fontSize: '18px',
                          userSelect: 'none',
                        }}
                      >
                        Expired
                      </span> */}
                    </div>
                  </Grid>
                </div>
              </Card>
              <Spacing lg="20" sm="20" />
            </>
          )
        }
        
        <BillingOverViewSection 
          billingOverviewDataCounts={billingOverviewDataCounts}
          billingOverviewLoader={billingOverviewLoader}
        />
        <Spacing lg="20" sm="20" />

        <div className="docpro_data_table_wrap">
          <div className="docpro_data_table_headings">
            <div>
              {/* <form className="docpro_search_form">
                <input
                  type="text"
                  placeholder="Search"
                  className="docpro_search_input"
                  onChange={handleChangeSearchTerm}
                />
                <button className="docpro_search_btn">
                  <Search width={20} height={20} />
                </button>
              </form> */}
            </div>

            {(can_visit("billing") && can_visit("billing_invoice_create")) && (
              <div className="docpro_data_table_btns p-0">
                {/* <button 
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={()=>generatePaymentLink("",1,'payment')}
                >
                  <Link width={20} height={20} />
                </button> */}
                <button
                  className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
                  onClick={createNewBillingInvoice}
                  disabled={generateBillLoader ? true : false}
                >
                  <FilePlus2 width={18} height={18} />
                  <span>Create Invoice</span>
                  {generateBillLoader && (
                    <span className="docpro_loading">
                      <Loader width={20} height={20} />
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          <InvoicesDataTable
            tableData={invoiceList || []}
            reloadData={loadAllInvoices}
            reloadDataFilter={filter}
            dataLoading={dataLoading}
            setDataLoading={setDataLoading}
            handleFormView={handleFormView}
            handleFormPrint={handleFormPrint}
            invoiceEdit={handleInvoiceEdit}
            handleDeleteInvoice={handleDeleteInvoice}
            handlePayInvoice={handlePayInvoice}
          />
          {(invoiceList.length > 0 && !dataLoading) && (
            <DataTablePagination
              currentPage={filter.page}
              totalPages={paginationCount.totalPages}
              onPageChange={handlePageChange}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              totalRecords={paginationCount.totalCount}
              currentPageData={filter.per_page}
            />
          )}
        </div>

        {pageLoader && (
          <div className="absolute top-0 left-0 w-full" style={{ height: '100%', zIndex: 3 }}>
            <div className="flex items-center loading" style={{ width: '100%', height: '100%', justifyContent: 'center', background: 'rgb(255 255 255 / 63%)' }}>
              <span className="docpro_loading" style={{ position: 'inherit' }}>
                <Loader width={46} height={46} style={{ color: 'var(--accent)' }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {formView && (
        <FormViewDetailsModal
          formId={formViewId}
          formSize={'A4'}
          formView={formView}
          setFormView={setFormView}
          formType={'invoice'}
          filterData={filter}
          reloadFunction={loadAllInvoices}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {paymentStatus && (
        <StatusViewModal
          openModal={paymentStatus}
          setOpenModal={setPaymentStatus}
          session={subscriptionSession}
          loadData={loadAllInvoices}
          filterData={filter}
          account_id={account_id}
          payment_type={subscriptionPaymentStatus}
          handleFormView={handleFormView}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {unsubscribeDocpro && (
        <CancelSubscriptionModal
          confirmAction={confirmUnsubscribe}
          openModal={unsubscribeDocpro}
          setOpenModal={setUnsubscribeDocpro}
          packageInfo={packageData}
          confirmUnsubscribeLoader={confirmUnsubscribeLoader}
          setConfirmUnsubscribeLoader={setConfirmUnsubscribeLoader}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {(invoiceCreate && can_visit("billing_invoice_create")) && (
        <CreateInvoiceModal
          reloadData={loadAllBillings}
          account_id={account_id}
          location_id={location_id}
          invoiceCreate={invoiceCreate}
          setInvoiceCreate={setInvoiceCreate}
          type={'add'}
          practiceBillingData={practiceBillingData}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {invoiceEdit && (
        <CreateInvoiceModal
          reloadData={loadAllBillings}
          account_id={account_id}
          location_id={location_id}
          invoiceCreate={invoiceEdit}
          setInvoiceCreate={setInvoiceEdit}
          type={'edit'}
          invoiceEditData={invoiceEditData}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {generateBillOpen && (
        <GenerateBillViewModal
          billLink={billLink}
          generateBillOpen={generateBillOpen}
          setGenerateBillOpen={setGenerateBillOpen}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
